import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnInit,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";

import { environment } from "../../../../environments/environment";
import { AuthenticationService } from "../../../core/_services/authentication.service";
import { LocalStorageService } from "../../../core/_services/browser-apis/local-storage.service";
import { WindowService } from "../../../core/_services/browser-apis/window.service";
import { NotificationService } from "../../../core/_services/notification.service";
import { isUserAdmin } from "../../../core/store/authentication";
import {
  availableProductsDetailsSelector,
  EditProductRequest,
  Product,
  ProductActions,
  productSelector,
} from "../../../core/store/product";
import { AppState } from "../../../core/store/store";
import { isUrlSafeForExternalOpen } from "../../../core/utils/url-security";
import { ClickOutsideDirective } from "../../_directives/click-outside.directive";

export enum HeaderMenus {
  UserProfile = "user-profile",
  Help = "help",
}

const imports = [CommonModule, ClickOutsideDirective];
@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  imports,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HeaderComponent implements OnInit {
  private authenticationService = inject(AuthenticationService);
  private store = inject<Store<AppState>>(Store);
  private windowService = inject(WindowService);
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);
  private notificationService = inject(NotificationService);

  developmentUrl = environment.developmentUrl;
  product$: Observable<Product | null> = this.store.select(productSelector);
  availableProductsDetails$: Observable<Product[] | null> = this.store.select(
    availableProductsDetailsSelector
  );
  visibleMenu: HeaderMenus | null = null;
  readonly HeaderMenus = HeaderMenus;
  isAdmin$ = this.store.select(isUserAdmin);

  authentication = inject(AuthenticationService);
  initialProduct: EditProductRequest | undefined;
  destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.store.dispatch(ProductActions.loadAvailableProductsDetails());
    this.store
      .select(productSelector)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(product => {
        if (product) {
          this.initialProduct = { ...product };
        }
      });
  }

  toggleVisibleMenu(clickedMenu: HeaderMenus | null, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.visibleMenu = this.visibleMenu === clickedMenu ? null : clickedMenu;
  }

  handleClickOutside(menu: HeaderMenus) {
    if (this.visibleMenu === menu) {
      this.visibleMenu = null;
    }
  }

  logout() {
    this.authenticationService.logout();
  }

  goToLink(url: string) {
    if (isUrlSafeForExternalOpen(url, environment.trustedDomains)) {
      this.windowService.locationRedirect(url);
    } else {
      console.error(`[Security] Blocked attempt to open untrusted URL: ${url}`);
      this.notificationService.showError(
        "Unable to open external link. Please contact support."
      );
    }
  }

  switchProduct(id: string) {
    const currentUrl = this.router.url;
    this.localStorageService.setItem("lastVisitedRoute", currentUrl);
    this.store.dispatch(ProductActions.switchProduct({ id }));
  }
}
